/**
 * SizilPro - StaticPageEditor
 * Step 4: Create and manage reusable pages (Our Process, About Us, etc.)
 *
 * Updated toward the new fixed-page direction:
 * - starter page presets
 * - fixed vs reusable page support
 * - page type + layout preset controls
 * - keeps current hook contract intact
 */

import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Plus,
  GripVertical,
  Trash2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Pin,
  FileText,
  LayoutTemplate,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import type {
  StaticPage,
  StaticPageKind,
  StaticPageLayoutPreset,
} from "../../types";
import {
  useStaticPages,
  useCreateStaticPage,
  useUpdateStaticPage,
  useDeactivateStaticPage,
} from "../../hooks/useStaticPages";
import { useDraftStaticPage } from "../../hooks/useAIWriter";
import { useBrandSettings } from "../../hooks/useBrandSettings";
import {
  SetupFieldGroup,
  SetupInput,
  SetupLabel,
  SetupTextarea,
  SetupActions,
} from "./SetupPrimitives";

const STARTER_PAGES: Array<{
  title: string;
  page_kind: StaticPageKind;
  layout_preset: StaticPageLayoutPreset;
  is_fixed: boolean;
  content_html: string;
}> = [
  {
    title: "Our Process",
    page_kind: "process",
    layout_preset: "standard",
    is_fixed: true,
    content_html: `We follow a consistent, professional process on every job.

• Initial walkthrough and needs assessment
• Custom scope of work developed for your property
• Dedicated crew assigned to your service plan
• Quality checks built into recurring service
• Ongoing communication and adjustments as needed`,
  },
  {
    title: "Why Choose Us",
    page_kind: "about",
    layout_preset: "standard",
    is_fixed: true,
    content_html: `We are a local team that takes pride in every job.

• Fully insured and background-checked staff
• Consistent crews every service visit
• Responsive communication within 24 hours
• Satisfaction-focused service approach
• Reliable follow-through and accountability`,
  },
  {
    title: "Service Guarantee",
    page_kind: "terms",
    layout_preset: "standard",
    is_fixed: true,
    content_html: `We stand behind the quality of our work and the consistency of our service.

• Clear scope of work before service begins
• Issues reported promptly are addressed quickly
• Consistent communication with your point of contact
• Service performed to the agreed standard every visit
• Reliable follow-through and accountability`,
  },
  {
    title: "What's Included",
    page_kind: "custom",
    layout_preset: "split",
    is_fixed: false,
    content_html: `Every proposal is built around a clear, repeatable scope so there is no confusion about what is being delivered.

• Defined service tasks and scope areas
• Agreed visit frequency and scheduling
• Attention to high-touch and high-visibility areas
• Team instructions aligned to your facility needs
• Consistent execution based on the approved scope`,
  },
  {
    title: "Communication Standards",
    page_kind: "custom",
    layout_preset: "standard",
    is_fixed: false,
    content_html: `We make communication simple, direct, and dependable.

• Clear point of contact for questions and updates
• Fast response times for service-related concerns
• Proactive communication about schedule or access issues
• Follow-up when something needs attention
• Professional communication with staff, managers, and owners`,
  },
  {
    title: "Safety & Compliance",
    page_kind: "custom",
    layout_preset: "standard",
    is_fixed: true,
    content_html: `We take safety, professionalism, and site standards seriously on every job.

• Team members trained for professional on-site conduct
• Careful attention to health, safety, and facility expectations
• Consistent procedures for shared and high-traffic spaces
• Respect for your property, staff, and operating hours
• Work performed with reliability and accountability`,
  },
  {
    title: "Why Clients Stay With Us",
    page_kind: "testimonial",
    layout_preset: "hero",
    is_fixed: false,
    content_html: `Our clients stay with us because we focus on consistency, responsiveness, and doing the work the right way.

• Reliable service without constant follow-up
• Crews that understand expectations and standards
• Quality checks and attention to detail
• Flexible support as needs change over time
• Long-term service built on trust and performance`,
  },
];


interface Props {
  onComplete: () => void;
}


function QuestionCard({
  number,
  title,
  description,
  children,
}: {
  number: number;
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6">
      <div className="mb-5 flex items-start gap-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-sm font-semibold text-black">
          {number}
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-white">{title}</h3>
          {description ? (
            <p className="mt-1 text-sm leading-6 text-white/45">{description}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}


export function StaticPageEditor({ onComplete }: Props) {
  const { data: pages = [], isLoading } = useStaticPages();
  const { data: brand } = useBrandSettings();
  const createPage = useCreateStaticPage();
  const updatePage = useUpdateStaticPage();
  const deactivatePage = useDeactivateStaticPage();
  const draftPage = useDraftStaticPage();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [newPageKind, setNewPageKind] = useState<StaticPageKind>("custom");
  const [newLayoutPreset, setNewLayoutPreset] =
    useState<StaticPageLayoutPreset>("standard");
  const [newIsFixed, setNewIsFixed] = useState(true);

  const isBusy =
    createPage.isPending || updatePage.isPending || deactivatePage.isPending;

  const handleDraftNewPage = async () => {
    if (!newTitle.trim()) return;
    const result = await draftPage.mutateAsync({
      pageTitle: newTitle.trim(),
      pageKind: newPageKind,
      businessContext: brand?.company_name ?? undefined,
    });
    setNewContent(result.content);
  };

  const handleAdd = async () => {
    if (!newTitle.trim() || !newContent.trim()) return;

    await createPage.mutateAsync({
      title: newTitle.trim(),
      content_html: newContent.trim(),
      page_order: pages.length,
      included_by_default: true,
      active: true,
      page_kind: newPageKind,
      is_fixed: newIsFixed,
      layout_preset: newLayoutPreset,
      style_overrides: {},
    });

    setNewTitle("");
    setNewContent("");
    setNewPageKind("custom");
    setNewLayoutPreset("standard");
    setNewIsFixed(true);
  };

  const handleAddStarter = async (starter: (typeof STARTER_PAGES)[number]) => {
    await createPage.mutateAsync({
      title: starter.title,
      content_html: starter.content_html,
      page_order: pages.length,
      included_by_default: true,
      active: true,
      page_kind: starter.page_kind,
      is_fixed: starter.is_fixed,
      layout_preset: starter.layout_preset,
      style_overrides: {},
    });
  };

  const availableStarters = STARTER_PAGES.filter(
    (starter) => !pages.some((page) => page.title === starter.title)
  );


  return (
    <div className="space-y-6">
      <div className="mb-2">
        <p className="text-sm leading-6 text-white/55">
          Build your library of reusable proposal pages - About Us, Our Process,
          Service Guarantee, and more. Add them to any proposal whenever you need them.
        </p>
      </div>

      {availableStarters.length > 0 && (
        <QuestionCard
          number={1}
          title="Start with pre-built pages"
          description="Pick a few common pages now, then edit them below."
        >
          <div className="flex flex-wrap gap-2">
            {availableStarters.map((starter) => (
              <button
                key={starter.title}
                type="button"
                disabled={createPage.isPending}
                onClick={() => void handleAddStarter(starter)}
                className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs transition-colors hover:bg-white/10 disabled:opacity-40"
              >
                <Plus className="h-3 w-3" />
                {starter.title}
              </button>
            ))}
          </div>

          <p className="mt-4 text-xs leading-6 text-white/35">
            These give you a clean base. You can edit the title and content on any page below.
          </p>
        </QuestionCard>
      )}

      <QuestionCard
        number={2}
        title="Review and edit your reusable pages"
        description="Edit the title and content of each page you've added."
      >
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-white/30" />
          </div>
        ) : pages.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-white/45">
            No pages added yet. Use a starter page or create one below.
          </div>
        ) : (
          <div className="space-y-2">
            {pages.map((page) => (
              <PageRow
                key={page.id}
                page={page}
                expanded={expandedId === page.id}
                isSaving={updatePage.isPending && updatePage.variables?.id === page.id}
                isDeleting={
                  deactivatePage.isPending && deactivatePage.variables === page.id
                }
                onToggle={() =>
                  setExpandedId((current) =>
                    current === page.id ? null : page.id
                  )
                }
                onDelete={() => deactivatePage.mutate(page.id)}
                onUpdate={(updates) => updatePage.mutate({ id: page.id, updates })}
              />
            ))}
          </div>
        )}
      </QuestionCard>

      <QuestionCard
        number={3}
        title="Add Page"
        description="Add a company-specific page to your proposal - About Us, Our Process, Service Guarantee, etc."
      >
        <div className="space-y-4">
          <SetupFieldGroup>
            <SetupLabel>Page title</SetupLabel>
            <SetupInput
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Our Process, About Us, Service Guarantee..."
            />
          </SetupFieldGroup>

          <SetupFieldGroup>
            <div className="flex items-center justify-between">
              <SetupLabel>Content</SetupLabel>
              {newTitle.trim() && (
                <button
                  type="button"
                  onClick={() => void handleDraftNewPage()}
                  disabled={draftPage.isPending}
                  className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/55 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
                >
                  {draftPage.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  Draft with AI
                </button>
              )}
            </div>
            <p className="mb-2 text-xs text-white/35">
              Keep it short - 3 to 5 bullet points or 2 short paragraphs max.
              This page appears near the end of your proposal. Your cover letter
              is where your full story lives.
            </p>

            <SetupTextarea
              rows={5}
              maxLength={1000}
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              placeholder={`We follow a consistent process on every job.

• Initial walkthrough
• Custom scope of work
• Dedicated crew
• Quality checks
• Clear communication`}
            />

            <p className="mt-1 text-right text-xs text-white/25">
              {newContent.length}/1000
            </p>
          </SetupFieldGroup>

          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={!newTitle.trim() || !newContent.trim() || createPage.isPending}
            className="flex items-center gap-2 rounded-lg bg-white/10 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-white/15 disabled:opacity-40"
          >
            {createPage.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            Add page
          </button>
        </div>
      </QuestionCard>

      <QuestionCard
        number={4}
        title="How these pages get used later"
        description="This is the handoff from setup into the rest of Proposal Builder."
      >
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-xl border border-white/10 bg-black/20 p-4">
            <Pin className="h-4 w-4 text-white/35" />
            <p className="mt-3 text-sm font-medium text-white/80">Brand Setup</p>
            <p className="mt-1 text-xs leading-6 text-white/40">
              This is where your reusable page library lives.
            </p>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 p-4">
            <LayoutTemplate className="h-4 w-4 text-white/35" />
            <p className="mt-3 text-sm font-medium text-white/80">Templates</p>
            <p className="mt-1 text-xs leading-6 text-white/40">
              Templates can include the pages you want for certain job types.
            </p>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/20 p-4">
            <FileText className="h-4 w-4 text-white/35" />
            <p className="mt-3 text-sm font-medium text-white/80">Proposals</p>
            <p className="mt-1 text-xs leading-6 text-white/40">
              Final proposals inherit those pages based on template and page rules.
            </p>
          </div>
        </div>
      </QuestionCard>

      <SetupActions>
        <button
          type="button"
          onClick={onComplete}
          disabled={isBusy}
          className="rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
        >
          {pages.length === 0 ? "Skip for now" : "Save & continue"}
        </button>
      </SetupActions>
    </div>
  );
}

function PageRow({
  page,
  expanded,
  isSaving,
  isDeleting,
  onToggle,
  onDelete,
  onUpdate,
}: {
  page: StaticPage;
  expanded: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onUpdate: (updates: Partial<StaticPage>) => void;
}) {
  const draftPage = useDraftStaticPage();
  const [title, setTitle] = useState(page.title);
  const [content, setContent] = useState(page.content_html);
  const [pageKind, setPageKind] = useState<StaticPageKind>(page.page_kind);
  const [layoutPreset, setLayoutPreset] = useState<StaticPageLayoutPreset>(
    page.layout_preset
  );
  const [isFixed, setIsFixed] = useState(page.is_fixed);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setTitle(page.title);
    setContent(page.content_html);
    setPageKind(page.page_kind);
    setLayoutPreset(page.layout_preset);
    setIsFixed(page.is_fixed);
    setDirty(false);
  }, [page]);

  return (
    <div className="overflow-hidden rounded-xl border border-white/10">
      <div className="flex items-center gap-3 px-4 py-3">
        <GripVertical className="h-4 w-4 cursor-grab text-white/20" />

        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center justify-between text-left"
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate text-sm font-medium">{page.title}</span>

            </div>
          </div>

          {expanded ? (
            <ChevronUp className="h-4 w-4 text-white/40" />
          ) : (
            <ChevronDown className="h-4 w-4 text-white/40" />
          )}
        </button>

        <button
          type="button"
          onClick={onDelete}
          disabled={isDeleting}
          className="rounded-md p-1.5 text-white/20 transition-colors hover:bg-red-500/10 hover:text-red-400 disabled:opacity-50"
        >
          {isDeleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-white/10 px-4 pb-4 pt-3">
          <SetupInput
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setDirty(true);
            }}
            placeholder="Page title"
          />

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs text-white/40">Content</span>
              <button
                type="button"
                onClick={async () => {
                  const result = await draftPage.mutateAsync({
                    pageTitle: title.trim() || page.title,
                    pageKind: pageKind,
                  });
                  setContent(result.content);
                  setDirty(true);
                }}
                disabled={draftPage.isPending}
                className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/55 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
              >
                {draftPage.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Sparkles className="h-3 w-3" />
                )}
                Draft with AI
              </button>
            </div>
            <SetupTextarea
              rows={5}
              maxLength={1000}
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setDirty(true);
              }}
            />
          </div>

          <p className="text-right text-xs text-white/25">{content.length}/1000</p>

          {dirty && (
            <button
              type="button"
              disabled={isSaving || !title.trim() || !content.trim()}
              onClick={() => {
                onUpdate({
                  title: title.trim(),
                  content_html: content.trim(),
                  page_kind: pageKind,
                  layout_preset: layoutPreset,
                  is_fixed: isFixed,
                });
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-1.5 text-xs transition-colors hover:bg-white/15 disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              {isSaving ? "Saving..." : "Save changes"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}